'use es6';

import { createErrorObject } from './errors';
import { ensureFn, createQueryString, createQueryObject } from './helpers';
import { DEBUG_MESSAGES } from '../messages';
import { ENDPOINT_EXPOSURE_BULK_LOG, ENDPOINT_TREATMENTS_BULK_GET } from '../constants';
const timeoutError = createErrorObject('api', 'request timeout');
const httpPromiseError = createErrorObject('api', 'internal hub-http error');
export function logExposures({
  apiDomain = '',
  exposures = [],
  http,
  onError,
  query = {},
  logDebug
} = {}) {
  const params = {
    data: exposures,
    query: createQueryObject(query)
  };
  const safeHttp = ensureFn(http, () => Promise.resolve({}));
  const safeError = ensureFn(onError);
  const totalExposures = params.data ? params.data.length : 0;

  const logTreatmentExposure = () => ensureFn(logDebug)(DEBUG_MESSAGES.treatmendExposed(totalExposures), 'api'); // Requests the resolution of the dynamic import and then acts on it
  // The whole logic is bounded with multiple fail-safes to ensure
  // That no exception happens and that the execution continues


  return safeHttp().then(resolvedHttp => {
    if (totalExposures === 0) {
      // We don't need to make `http` requests if we have 0 treatments to be exposed
      // Or if somehow the data is malformed. This is different from the other methods
      // As here `ceid` doesn't make a difference at all.
      return undefined;
    }

    const endpoint = `${apiDomain}/${ENDPOINT_EXPOSURE_BULK_LOG}`;
    const request = ensureFn(resolvedHttp.post)(endpoint, params);

    if (request instanceof Promise) {
      return request.then(logTreatmentExposure).catch(safeError);
    }

    return safeError(httpPromiseError);
  });
}
export function getTreatmentsFromHttp({
  apiDomain = '',
  data = [],
  http,
  timeout,
  onComplete,
  onError,
  query = {}
} = {}) {
  const params = {
    timeout,
    data,
    query: createQueryObject(query)
  };
  const safeHttp = ensureFn(http, () => Promise.resolve({}));
  const safeError = ensureFn(onError); // Requests the resolution of the dynamic import and then acts on it
  // The whole logic is bounded with multiple fail-safes to ensure
  // That no exception happens and that the execution continues

  return safeHttp().then(resolvedHttp => {
    // Here we don't calculate if the `params.data.length` is empty
    // As for now we have the `ceid` parameter, that might be used
    // Even if the `data` being empty resulting on a network request being needed
    const endpoint = `${apiDomain}/${ENDPOINT_TREATMENTS_BULK_GET}`;
    const request = ensureFn(resolvedHttp.post)(endpoint, params);

    if (request instanceof Promise) {
      return request.then(ensureFn(onComplete)).catch(safeError);
    }

    return safeError(httpPromiseError);
  });
}
export function getTreatmentsFromQuickFetch({
  apiDomain = '',
  data = [],
  quickFetch,
  quickFetchLabel,
  timeout,
  onComplete,
  onError,
  query = {}
} = {}) {
  const safeQuickFetch = ensureFn(quickFetch, () => Promise.resolve({}));
  const safeError = ensureFn(onError); // Requests the resolution of the dynamic import and then acts on it
  // The whole logic is bounded with multiple fail-safes to ensure
  // That no exception happens and that the execution continues

  return safeQuickFetch().then(resolvedQuickFetch => {
    const safeGetRequestData = ensureFn(resolvedQuickFetch.getRequestStateByName);
    let requestState = safeGetRequestData(quickFetchLabel); // We don't want to execute the quickFetch query again if it was either already executed within laboratory-lib
    // or if it was executed within the codebase (earlyRequester) of the consuming application.

    if (!requestState) {
      const queryString = createQueryString(query);
      ensureFn(resolvedQuickFetch.makeEarlyRequest)(quickFetchLabel, {
        url: `${apiDomain}/${ENDPOINT_TREATMENTS_BULK_GET}${queryString}`,
        data: JSON.stringify(data),
        dataType: 'json',
        contentType: 'application/json',
        type: 'POST',
        timeout
      }); // Update the requestState with the new request state from quickFetch

      requestState = safeGetRequestData(quickFetchLabel);
    }

    if (requestState && typeof requestState === 'object') {
      const quickFetchTimeout = setTimeout(() => safeError(timeoutError), timeout); // This ensures that the contexts are respected as `requestState` methods
      // self reference the `requestState` object and access entries from it.

      const onErrorCallback = ensureFn(requestState.onError).bind(requestState);
      const whenFinishedCallback = ensureFn(requestState.whenFinished).bind(requestState);
      whenFinishedCallback(treatments => {
        clearTimeout(quickFetchTimeout);
        ensureFn(onComplete)(treatments);
      });
      onErrorCallback((_xhr, err) => {
        clearTimeout(quickFetchTimeout);
        safeError(err);
      });
    }
  });
}